import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, LinearProgress, Paper, Stack, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { apiRequest } from '../Utils/apiClient';

const PlatformStartupSettings = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [scriptPath, setScriptPath] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await apiRequest('/api/v1/saas/platform/startup');
      setSettings(result);
      setScriptPath(result.script_path);
    } catch (err) {
      setError('saas.platform_startup.load_error');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async event => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const result = await apiRequest('/api/v1/saas/platform/startup', {
        method: 'PUT', body: JSON.stringify({ script_path: scriptPath.trim() })
      });
      setSettings(result);
      setScriptPath(result.script_path);
      setSaved(true);
    } catch (err) {
      setError(err.code === 'invalid_startup_path'
        ? 'saas.platform_startup.path_error' : 'saas.platform_startup.save_error');
    } finally {
      setSaving(false);
    }
  };

  return <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3 }}>
    <Stack component="form" spacing={2} onSubmit={save}>
      <Typography component="h2" variant="h6">{t('saas.platform_startup.title')}</Typography>
      <Typography color="text.secondary">{t('saas.platform_startup.description')}</Typography>
      {loading && <LinearProgress aria-label={t('saas.platform_startup.loading')} />}
      {error && <Alert severity="error">{t(error)}</Alert>}
      {!loading && !settings && <Button onClick={load} sx={{ alignSelf: 'flex-start' }}>{t('saas.platform_startup.retry')}</Button>}
      {settings && <>
        {settings.approval_required && <Alert severity="warning">{t('saas.platform_startup.approval_required')}</Alert>}
        <TextField
          fullWidth
          label={t('saas.platform_startup.path')}
          value={scriptPath}
          placeholder={settings.default_script_path}
          helperText={t('saas.platform_startup.default_help', { path: settings.default_script_path })}
          FormHelperTextProps={{ sx: { overflowWrap: 'anywhere' } }}
          autoComplete="off"
          inputProps={{ maxLength: 4096, spellCheck: false }}
          disabled={saving}
          onChange={event => { setScriptPath(event.target.value); setSaved(false); setError(''); }}
        />
        {saved && <Alert severity="success" role="status">{t('saas.platform_startup.saved')}</Alert>}
        <Button type="submit" variant="contained" disabled={saving || (scriptPath.trim() === settings.script_path && !settings.approval_required)} sx={{ alignSelf: 'flex-start' }}>
          {t(saving ? 'saas.platform_startup.saving' : 'saas.platform_startup.save')}
        </Button>
      </>}
    </Stack>
  </Paper>;
};

export default PlatformStartupSettings;
